import React, {useContext, useEffect, useRef, useState} from "react"
import Row from "@amzn/meridian/row"
import Column from "@amzn/meridian/column"
import Select, {SelectOption} from "@amzn/meridian/select"
import RefreshTimer from "../../util/RefreshTimer";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {FormattedMessage, injectIntl} from "react-intl";
import {getRefreshInterval} from "../../util/Util";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import AddAslGuidedDivertConfigurationModal from "./modal/AddAslGuidedDivertConfigurationModal";
import Alert from "@amzn/meridian/alert";
import {AslDeviceOverviewContext} from "./context/AslDeviceOverviewContext";
import Loader from "../../loader/Loader";
import Tooltip from "@amzn/meridian/tooltip";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import DeleteAslGuidedDivertConfigurationModal from "./modal/DeleteAslGuidedDivertConfigurationModal";
import AddEditViaJsonModal from "../../commonui/AddEditViaJsonModal";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";

const AslDeviceConfiguration = ({intl}) => {

    const {viewModel, dispatch} = useContext(AslDeviceOverviewContext);
    const [valueConfiguration, setValueConfiguration] = useState("Guided Divert Configuration")
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [selectedMac, setSelectedMac] = useState("")
    const tableEndRef = useRef(null)

    useEffect(() => {
        getData()
        const interval = setInterval(() => {
            if (!document.hidden) {
                getData();
            }
        }, getRefreshInterval());
        return () => clearInterval(interval);
    }, []);

    const sortedDeviceList = Object.values(viewModel.aslDeviceInformation);

    const getData = () => {
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }
    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    function onClickAddSingle() {
        setOpenAddModal(true)
    }

    function onClickDeleteConfiguration() {
        setOpenDeleteModal(true)
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    }
    return (
        <Column>
            <Box type="fill" spacingInset="medium">
                <Row width="100%" widths={["100", "fill", "100", "fill"]}>
                    <Row>
                        <Text>
                            <FormattedMessage id="configtype.title" defaultMessage="Select Config Type"/>
                        </Text>
                        <Select
                            value={valueConfiguration}
                            onChange={setValueConfiguration}
                            placeholder="Select Config Type"
                            width={300}
                        >
                            <SelectOption key="Guided Divert Configuration" value="Guided Divert Configuration"
                                          label="Guided Divert Configuration"/>
                        </Select>
                    </Row>
                    <Box></Box>
                    {valueConfiguration === "Guided Divert Configuration" &&
                        <Row>
                            <Text>
                                <FormattedMessage id="devicename.title" defaultMessage="Select Device"/>
                            </Text>
                            <Select
                                value={selectedMac}
                                onChange={setSelectedMac}
                                placeholder="Select Device"
                                width={300}>
                                {sortedDeviceList.map((device) => (
                                    <SelectOption key={device.mac} value={device.mac}
                                                  label={device.name + " (" + device.mac + ")"}/>
                                ))}
                                {!sortedDeviceList.length ? (
                                    <Column alignmentVertical="center" spacing="100" spacingInset="100">
                                        <Text alignment="center"> No devices available </Text>
                                    </Column>) : null}
                            </Select>
                        </Row>}
                    <Box></Box>
                </Row>
            </Box>
            {valueConfiguration === "Guided Divert Configuration" && selectedMac && <Column>
                <Row width="100%" widths={["100", "fill", "100"]}>
                    <RefreshTimer deps={[viewModel]}/>
                    <Box></Box>
                    <Tooltip position="top" title={intl.formatMessage({
                        id: 'tooltip.scrolltobottom',
                        defaultMessage: 'Scroll to bottom'
                    })}>
                        <Button onClick={scrollToBottom}
                                type="secondary"
                                size="small">
                            <Icon tokens={chevronDownLargeTokens}/>
                        </Button>
                    </Tooltip>
                </Row>
                {(viewModel.aslDeviceInformation[selectedMac].divertConfig).length === 0 ?
                    <Alert type="informational" size="small">
                        <FormattedMessage id="alert.config"
                                          defaultMessage=" No Configurations present for this MAC Address."/>
                    </Alert> :
                    <Table
                        headerRows={1}
                        spacing="small"
                        showDividers={true}
                        showStripes={true}>
                        <TableRow highlightOnHover={false}>
                            <TableCell>
                                <FormattedMessage id="bay.index" defaultMessage="Index"/>
                            </TableCell>
                            <TableCell>
                                <FormattedMessage id="modal.divertcode" defaultMessage="Divert Code"/>
                            </TableCell>
                            <TableCell>
                                <FormattedMessage id="slam.clusters" defaultMessage="Clusters"/>
                            </TableCell>
                        </TableRow>
                        {Object.values(viewModel.aslDeviceInformation[selectedMac].divertConfig.map((device, index) => (
                            <TableRow highlightOnHover={true}>
                                <TableCell>
                                    {index + 1}
                                </TableCell>
                                <TableCell>
                                    {device.divertCode}
                                </TableCell>
                                <TableCell>
                                    {device.clusters}
                                </TableCell>
                            </TableRow>
                        )))}
                    </Table>}
                <Row width="100%" widths={["100", "100", "fill", "100"]}>
                    <Button onClick={onClickAddSingle}
                            type="primary"
                            size="small">
                        <Icon tokens={plusCircleTokens}/>
                        <FormattedMessage id="config.addupdateasl"
                                          defaultMessage="Add/Update Guided Divert Configuration"/>
                    </Button>
                    <AddEditViaJsonModal/>
                    <Box></Box>
                    <Button onClick={onClickDeleteConfiguration}
                            type="tertiary"
                            size="small">
                        <Icon tokens={trashTokens}/>
                        <FormattedMessage id="config.delete" defaultMessage="Delete Configuration"/>
                    </Button>
                </Row>
                <AddAslGuidedDivertConfigurationModal mac={selectedMac}
                                                      open={openAddModal} setOpen={setOpenAddModal}/>
                <DeleteAslGuidedDivertConfigurationModal mac={selectedMac}
                                                         open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                <div ref={tableEndRef}/>
            </Column>}
        </Column>
    );
}


export default injectIntl(AslDeviceConfiguration)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/asldevice/AslDeviceConfiguration.js